<!-- components/settings/SalesPlatformManagement.vue -->
<template>
  <div class="platform-management">
    <var-table class="platform-table" :elevation="0" scroller-height="100%">
      <thead>
        <tr>
          <th style="text-align:center;width:60px;">ID</th>
          <th style="text-align:center;width:80px;">图标</th>
          <th style="text-align:left;">名称</th>
          <th style="text-align:center;">编码</th>
          <th style="text-align:center;width:160px;">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="platforms.length === 0">
          <td colspan="5" class="empty-text">暂无销售平台</td>
        </tr>
        <tr v-for="item in platforms" :key="item.id">
          <td style="text-align:center;">{{ item.id }}</td>
          <td style="text-align:center;font-size:20px;">{{ item.icon || '📦' }}</td>
          <td>{{ item.name }}</td>
          <td style="text-align:center;"><var-chip size="small" type="primary">{{ item.code }}</var-chip></td>
          <td style="text-align:center;">
            <var-space :size="4">
              <var-button size="small" type="warning" plain @click="openForm(item)">编辑</var-button>
              <var-button size="small" type="danger" plain @click="handleDelete(item)">删除</var-button>
            </var-space>
          </td>
        </tr>
      </tbody>
    </var-table>

    <!-- 表单弹窗 -->
    <var-dialog v-model:show="showFormModal" :title="formMode === 'edit' ? '✏️ 编辑销售平台' : '➕ 新增销售平台'" width="440px"
      @confirm="handleFormSubmit" :confirm-loading="formLoading">
      <div style="padding:8px 0;">
        <var-space direction="column" :size="12" style="width:100%;">
          <var-space direction="column" :size="4" style="width:100%;">
            <span class="field-label">平台名称 <span style="color:#f56c6c;">*</span></span>
            <var-input v-model="form.name" placeholder="请输入平台名称" />
          </var-space>

          <var-space direction="column" :size="4" style="width:100%;">
            <span class="field-label">平台编码 <span style="color:#f56c6c;">*</span></span>
            <var-input v-model="form.code" placeholder="请输入平台编码（如 temu, shein）" />
            <span style="font-size:11px;color:#999;">💡 编码用于系统识别，建议使用英文小写</span>
          </var-space>

          <var-space direction="column" :size="4" style="width:100%;">
            <span class="field-label">图标（Emoji）</span>
            <var-input v-model="form.icon" placeholder="请输入图标 Emoji，如 🛒" />
            <span style="font-size:11px;color:#999;">💡 可选，用于界面显示</span>
          </var-space>
        </var-space>
      </div>
    </var-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { Snackbar, Dialog } from '@varlet/ui'
import { useSalesPlatformStore } from '@/stores'

const salesPlatformStore = useSalesPlatformStore()
const platforms = computed(() => salesPlatformStore.platforms)

const showFormModal = ref(false)
const formMode = ref('add')
const formLoading = ref(false)
const form = ref({
  id: null,
  name: '',
  code: '',
  icon: ''
})

// ===== 打开表单 =====
function openForm(item = null) {
  if (item) {
    formMode.value = 'edit'
    form.value = {
      id: item.id,
      name: item.name || '',
      code: item.code || '',
      icon: item.icon || ''
    }
  } else {
    formMode.value = 'add'
    form.value = {
      id: null,
      name: '',
      code: '',
      icon: ''
    }
  }
  showFormModal.value = true
}

// ===== 提交表单 =====
async function handleFormSubmit() {
  const data = form.value

  if (!data.name || !data.name.trim()) {
    Snackbar.error('请输入平台名称')
    return false
  }

  if (!data.code || !data.code.trim()) {
    Snackbar.error('请输入平台编码')
    return false
  }

  // 编码只允许小写字母、数字和下划线
  if (!/^[a-z][a-z0-9_]*$/.test(data.code)) {
    Snackbar.error('编码只能包含小写字母、数字和下划线，且必须以字母开头')
    return false
  }

  formLoading.value = true

  try {
    let result
    if (formMode.value === 'edit') {
      result = await salesPlatformStore.updatePlatform(data)
    } else {
      result = await salesPlatformStore.createPlatform(data)
    }

    if (result) {
      Snackbar.success(formMode.value === 'edit' ? '更新成功' : '创建成功')
      showFormModal.value = false
    }
  } catch (err) {
    Snackbar.error(err.message || '操作失败')
    return false
  } finally {
    formLoading.value = false
  }
}

// ===== 删除 =====
async function handleDelete(item) {
  if (await Dialog({
    title: '确认删除',
    message: `确定要删除「${item.name}」吗？`,
    confirmButtonText: '确认删除'
  }) !== 'confirm') {
    return
  }

  try {
    const success = await salesPlatformStore.deletePlatform(item.id)
    if (success) {
      Snackbar.success('删除成功')
    }
  } catch (err) {
    Snackbar.error(err.message || '删除失败')
  }
}

// ===== 加载数据 =====
onMounted(() => {
  salesPlatformStore.loadPlatforms()
})

// ===== 暴露给父组件 =====
defineExpose({
  openForm
})
</script>

<style scoped>
.platform-management {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.platform-table {
  flex: 1;
  overflow: auto;
  margin-top: 12px;
}

.platform-table :deep(.var-table) {
  flex: 1;
  overflow: auto;
}

.empty-text {
  text-align: center;
  padding: 40px 0;
  color: #999;
}
</style>